import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import styled from "styled-components";

import AuthContext from "common/store/AuthContext";
import StatusContext from "common/store/StatusContext";

import { WeatherData } from "common/weather/types/WeatherData";
import { SaveDataHandler } from "common/weather/types/SaveDataHandler";

import { useWeather } from "./hooks/useWeather";
import { REPORT_TYPE } from "common/types/consts/Defines";
import MyTextbox from "common/components/atoms/MyTextbox";
import { Form } from "react-bootstrap";
import { NameID } from "common/types/NameID";
import FunctionButton from "common/components/atoms/FunctionButton";
import MyNumberInput from "common/components/atoms/MyNumberInput";

type Props = { reportType: REPORT_TYPE };

export const WeatherCtrl = forwardRef<SaveDataHandler, Props>((props: Props, ref) => {
  const { reportType } = props;
  const { selectedPark } = useContext(AuthContext);
  const { isEditing, currentDate, setErrorMessage } = useContext(StatusContext);

  // 初期データと編集したデータの両方を持ち、必要な時だけ保存を行う
  const [initialData, setInitialData] = useState<WeatherData>();

  const { getWeatherPatterns, getWeatherData, saveWeatherData } = useWeather();
  const [data, setData] = useState<WeatherData>();
  const [presetWeathers, setPresetWeathers] = useState<NameID[]>([]);

  const isDataSame = (data1: WeatherData | undefined, data2: WeatherData | undefined) => {
    if (data1 && data2) {
      return JSON.stringify(data1) === JSON.stringify(data2);
    }
    return false;
  };

  useImperativeHandle(ref, () => ({
    async save() {
      if (!selectedPark) return;
      if (isDataSame(initialData, data)) return;

      const f = async () => {
        const { msg, succeeded } = await saveWeatherData(selectedPark.parkId, currentDate, data);
        if (!succeeded) setErrorMessage(msg);
      };
      f();
    },
    canecl() {
      setData(initialData ? { ...initialData } : undefined);
    },
  }));

  //--------------------------------------
  // 初期化処理： メタデータ取得 → 日報データ取得
  //--------------------------------------
  /**
   * 天気情報を取得する
   * @param repType 明示的に日報・作業日報を指定する
   */
  const loadWeather = async (repType?: REPORT_TYPE) => {
    if (!selectedPark) {
      setErrorMessage("公園が設定されていません");
      return;
    }

    if (presetWeathers.length === 0) {
      const { data: patterns } = await getWeatherPatterns();
      if (patterns) setPresetWeathers(patterns.weatherPatterns);
    }

    const res = await getWeatherData(selectedPark.parkId, currentDate, reportType);
    // 取得成功
    if (res.data) {
      setData(res.data);
      setInitialData(res.data);
      return;
    }
    // 取得エラー (サーバーダウンなど)
    if (!res.succeeded) {
      setErrorMessage(res.msg);
      setData(undefined);
      setInitialData(undefined);
      return;
    }

    // データが未登録の場合は、もう片方の記録から持ってくる
    const alternateType = reportType === REPORT_TYPE.DAILY ? REPORT_TYPE.WORK : REPORT_TYPE.DAILY;
    const res2 = await getWeatherData(selectedPark.parkId, currentDate, alternateType);
    if (res2.succeeded) {
      // 保存時に正しく保存されるようになる
      setData(res2.data);
      setInitialData(undefined);
      return;
    }
  };

  useEffect(() => {
    const f = async () => {
      await loadWeather();
    };

    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  /**
   * APIの天気データを手動入力の方に反映する
   */
  const applyApiWeatherToSelect = () => {
    const apiWeatherName = data?.apiData?.weather?.name;
    if (!apiWeatherName) return;

    // プリセットから一致するものを探す
    const matchedPreset = presetWeathers.find((preset) => preset.name === apiWeatherName);

    if (matchedPreset) {
      // プリセットに一致する場合
      setData({
        ...data,
        weatherId: matchedPreset.id,
        weather: matchedPreset.name,
      });
    } else {
      // プリセットに一致しない場合は「その他」として設定
      setData({
        ...data,
        weatherId: -1,
        weather: apiWeatherName,
      });
    }
  };

  const applyApiTemp = () => {
    if (!data?.apiData?.avgTemperature) return;
    setData({
      ...data,
      temperature: data.apiData.avgTemperature,
    });
  };

  const getFormattedValue = (unit: string, value?: number, digits = 1) => {
    if (value === undefined) return "なし";
    return `${value.toFixed(digits)} ${unit}`;
  };

  return (
    <SRootDiv>
      {/* マニュアルの天気 */}
      <SRowDiv>
        {/* 天気の入力 */}
        <SGroupDiv>
          <SLabel isEditing={isEditing}>天気：</SLabel>
          {isEditing ? (
            <>
              <Form.Select
                value={data?.weatherId ?? -2}
                style={{ minWidth: "140px", fontSize: "14px", padding: "3.2px 4px" }}
                onChange={(e) => {
                  e.preventDefault();
                  const id = +e.currentTarget.value;
                  const name = e.currentTarget.selectedOptions[0].label;
                  setData({ ...data, weatherId: id, weather: name });
                }}
              >
                <option value={-2}>記録なし</option>
                {presetWeathers.map((x) => (
                  <option key={`pattern_${x.id}`} value={x.id}>
                    {x.name}
                  </option>
                ))}
                <option value={-1}>その他</option>
              </Form.Select>
              {data?.weatherId === -1 && (
                <MyTextbox
                  initialValue={data.weather ?? ""}
                  changed={(e) => {
                    setData({ ...data, weather: e });
                  }}
                  readonly={false}
                />
              )}
            </>
          ) : (
            <SLabel>{data?.weather ?? "記録なし"}</SLabel>
          )}
        </SGroupDiv>

        {/* クリアーボタン */}
        {isEditing && (
          <FunctionButton
            size="sm"
            tooltip="クリアー"
            onClick={() => setData({ ...data, weatherId: undefined, weather: undefined })}
          >
            <i className="fas fa-eraser" />
          </FunctionButton>
        )}
        {/* 気温の入力 */}
        <SGroupDiv>
          <SLabel isEditing={isEditing}>気温：</SLabel>
          {isEditing ? (
            <>
              <MyNumberInput
                type="float"
                initialValue={data?.temperature ?? 0}
                min={-99.9}
                max={99.9}
                step={0.1}
                changed={(value) => setData({ ...data, temperature: value })}
              />
              ℃
            </>
          ) : (
            <SLabel>{data?.temperature ? `${data.temperature} ℃` : "記録なし"}</SLabel>
          )}
        </SGroupDiv>
      </SRowDiv>

      {/* APIの天気 */}
      {data?.apiData && (
        <SRowDiv style={{ gap: 0 }}>
          <SApiLabel>
            {"("}
            {isEditing ? (
              <SApiLink onClick={applyApiWeatherToSelect}>
                {data.apiData.weather?.name ?? "-"}
              </SApiLink>
            ) : (
              (data.apiData.weather?.name ?? "-")
            )}
            {"、平均: "}
            {isEditing ? (
              <SApiLink onClick={applyApiTemp}>
                {getFormattedValue(`℃`, data.apiData.avgTemperature)}
              </SApiLink>
            ) : (
              getFormattedValue(`℃`, data.apiData.avgTemperature)
            )}
            {"、"}
            {`最高: ${getFormattedValue(
              `℃`,
              data.apiData.maxTemperature,
            )}、最低: ${getFormattedValue(`℃`, data.apiData.minTemperature)}、`}
            {`降水: ${getFormattedValue("mm", data.apiData.rain)}、
            降雪: ${getFormattedValue("mm", data.apiData.snow)})`}
          </SApiLabel>
          <SApiLabel style={{ fontSize: "x-small", alignSelf: "flex-end", marginLeft: "0.3rem" }}>
            {" by 日本気象協会"}
          </SApiLabel>
        </SRowDiv>
      )}
    </SRootDiv>
  );
});

const SRootDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const SRowDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 10px;
`;

const SGroupDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: flex-start;
`;

const SLabel = styled.h6<{ isEditing?: boolean }>`
  min-width: ${(props) => (props.isEditing ? "52px" : "auto")};
`;

const SApiLabel = styled.h6`
  font-size: smaller;
  margin: 0;
`;

const SApiLink = styled.span`
  color: #0066cc;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #0052a3;
    text-decoration: none;
  }
`;
